/**
 * 匿名 AI 安全客户端（Aibi 兼容）
 *
 * 安全目标：
 * 1. 请求不可伪造（HMAC）
 * 2. 防重放（nonce + timestamp）
 * 3. 抗刷（PoW）
 * 4. 设备 / 环境绑定（env_fp）
 *
 * 说明：
 * - prompt 明文发送，便于后端做内容审计
 * - 加密/签名内容不包含 prompt
 */

import CryptoJS from 'crypto-js';

/* ================= 工具方法 ================= */

/**
 * Proof of Work
 * 要求 hash(seed + nonce) 以 difficulty 个 0 开头
 * 用于提高脚本攻击成本
 */
function solvePoW(seed: string, difficulty: number): number {
  let nonce = 0;
  const prefix = '0'.repeat(difficulty);

  while (true) {
    const hash = CryptoJS.SHA256(seed + nonce).toString();
    if (hash.startsWith(prefix)) return nonce;
    nonce++;
  }
}

/**
 * 生成匿名环境指纹（不可逆）
 * 用于设备绑定，不暴露隐私
 */
function collectEnvFP(): string {
  const raw = [
    navigator.userAgent,
    navigator.languages?.join(','),
    navigator.hardwareConcurrency,
    navigator.webdriver ? '1' : '0',
  ].join('|');

  return CryptoJS.SHA256(raw).toString();
}

/**
 * HMAC-SHA256 签名
 * 保证请求不可伪造、不可篡改
 */
function signPayload(payload: any, secret: string): string {
  // 排序 + 紧凑 JSON，确保前后端一致
  const text = JSON.stringify(payload, Object.keys(payload).sort());
  return CryptoJS.HmacSHA256(text, secret).toString();
}

export const getCryptoHeaders = (data: {
  nonce: string;
  salt: string;
  pow_seed: string;
  difficulty: number;
}) => {
  const { nonce, salt, pow_seed, difficulty } = data;

  // 2️⃣ 计算 Proof of Work（算力成本）
  const pow_nonce = solvePoW(pow_seed, difficulty);

  // 3️⃣ 构造签名数据（不包含 prompt）
  const timestamp = Date.now();
  const env_fp = collectEnvFP();

  const signData = {
    nonce, // 防重放
    timestamp, // 过期控制
    env_fp, // 设备绑定
  };

  // 4️⃣ 派生密钥（后端可重建）
  // salt 来自后端，UA 双方可见
  const secret = salt + navigator.userAgent;

  // 5️⃣ 计算签名
  const signature = signPayload(signData, secret);
  return {
    'Content-Type': 'application/json',
    'X-Nonce': nonce,
    'X-Timestamp': timestamp.toString(),
    'X-Env-FP': env_fp,
    'X-Signature': signature,
    'X-Pow-Seed': pow_seed,
    'X-Pow-Nonce': pow_nonce.toString(),
  };
};

/** 设备指纹 */
export const DEVICE_FINGERPRINT = collectEnvFP();
